import React, { useEffect, useState } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';
import { SecondaryBase } from './../Commons/SecondaryBase';
import { Confirm } from './../../Commons/Modals/Confirm';
import { PremiumQR } from './../../Commons/PremiumQR';
import { 
    GetThemes, 
    GetTheme,
    SaveTheme, 
    DeleteTheme, 
    GetConfig, 
    SaveConfig, 
    GetActiveScreens, 
    CreateMachineScreen, 
    DeleteMachineScreen, 
    IdentifyAllScreens, 
    UploadMedia,
    PrepareMachine,
    SpinMachine,
    ShowCover,
    StopReel
} from './../../../utilities/BL/Rollers';

// Componente helper para subida de archivos moderna con Drag & Drop
const DragAndDropField = ({ label, targetField, type, value, onUpload, getMediaUrl }) => {
    const [isDragOver, setIsDragOver] = useState(false);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            onUpload(file, type, targetField);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            onUpload(file, type, targetField);
        }
    };

    return (
        <div className="mb-3">
            {label && <label className="form-label fw-bold text-dark">{label}:</label>}
            <div 
                className={`border border-2 border-dashed rounded-3 p-3 text-center cursor-pointer transition ${isDragOver ? 'border-primary bg-light' : 'border-secondary bg-white'}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById(`file-input-${targetField}`).click()}
            >
                <i className="bi bi-cloud-arrow-up-fill text-primary fs-3"></i>
                <p className="small text-muted mb-0 mt-1">Arrastra y suelta tu archivo aquí, o haz clic para examinar</p>
                <input 
                    id={`file-input-${targetField}`}
                    type="file" 
                    className="d-none"
                    onChange={handleFileChange}
                />
            </div>
            {value && (
                <div className="mt-2 text-center bg-light p-2 rounded border position-relative">
                    {type === 'logo' || value.endsWith('.png') || value.endsWith('.jpg') || value.endsWith('.jpeg') ? (
                        <img src={getMediaUrl(value)} alt="Preview" style={{ maxHeight: '60px', objectFit: 'contain' }} />
                    ) : (
                        <span className="small text-truncate d-block text-dark">{value.split('/').pop()}</span>
                    )}
                </div>
            )}
        </div>
    );
};

export const ArmaLaImagen = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();
    const [isLoading, setIsLoading] = useState(false);
    const [lastRefresh, setLastRefresh] = useState('');

    const [themesList, setThemesList] = useState([]);
    const [screensList, setScreensList] = useState([]);
    const [adminSpinStates, setAdminSpinStates] = useState({});
    
    // Control de Acordeones
    const [isMainExpanded, setIsMainExpanded] = useState(true);
    const [isStandsExpanded, setIsStandsExpanded] = useState(false);

    // Modales
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({ message: '', action: null, textYes: '', textNo: 'Cancelar' });
    const [showThemeModal, setShowThemeModal] = useState(false);
    const [showQRModal, setShowQRModal] = useState(false);
    const [qrTargetUrl, setQrTargetUrl] = useState('');
    const [tvTargetUrl, setTvTargetUrl] = useState('');
    const [qrTitle, setQrTitle] = useState('');

    // Configuración Estadio (ID 1 Fijo)
    const [mainConfig, setMainConfig] = useState({ targetScreenId: 1, themeId: '', winProbability: 30, baseTimeMs: 2200, celebrationTimeMs: 6000, scale: 100, rollersCount: 3, speeds: '' });
    const [showCustomMainProb, setShowCustomMainProb] = useState(false);
    const [showCustomMainTimes, setShowCustomMainTimes] = useState(false);

    // Configuración Stands (ID > 1)
    const [selectedStandId, setSelectedStandId] = useState(0);
    const [standConfig, setStandConfig] = useState({ targetScreenId: 0, themeId: '', winProbability: 30, baseTimeMs: 2200, celebrationTimeMs: 6000, scale: 100, rollersCount: 3, speeds: '' });
    const [showCustomStandProb, setShowCustomStandProb] = useState(false);
    const [showCustomStandTimes, setShowCustomStandTimes] = useState(false);

    const [tempInputs, setTempInputs] = useState({});

    // Estado del Tema en Edición
    const defaultThemeState = {
        id: 0,
        name: '',
        brandLogoUrl: '',
        sponsorLogoUrl: '',
        bulbColors: '#ffff00,#ff0000,#00ffff',
        rollerImages: [],
        coverBackgroundType: 'color',
        coverBackgroundColor: '#000000',
        coverBackgroundUrl: '',
        gameBackgroundType: 'color',
        gameBackgroundColor: '#121212',
        gameBackgroundUrl: '',
        machineBackgroundType: 'color',
        machineBackgroundColor: '#ffffff',
        machineBackgroundUrl: '',
        fanBackgroundType: 'color',
        fanBackgroundColor: '#1a1a1a',
        fanBackgroundUrl: ''
    };
    const [themeForm, setThemeForm] = useState(defaultThemeState);

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        setIsLoading(true);
        try {
            const themesRes = await GetThemes();
            if (themesRes.success) setThemesList(themesRes.data);

            const screensRes = await GetActiveScreens();
            if (screensRes.success) {
                setScreensList(screensRes.data);
                
                const mainRes = await GetConfig(1);
                if (mainRes.success && mainRes.data) {
                    setMainConfig({
                        ...mainRes.data,
                        winProbability: Math.round(mainRes.data.winProbability * 100),
                        scale: mainRes.data.scale || 100,
                        themeId: mainRes.data.themeId || ''
                    });
                }

                const stands = screensRes.data.filter(s => s.id !== 1);
                if (stands.length > 0) {
                    if (!stands.find(s => s.id === selectedStandId)) {
                        setSelectedStandId(stands[0].id);
                        loadConfigForStand(stands[0].id);
                    }
                } else {
                    setSelectedStandId(0);
                }
            }
        } catch (error) {
            showMessage('Error al cargar datos iniciales', MESSAGE_TYPE.ERROR);
        } finally {
            setIsLoading(false);
            setLastRefresh(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
        }
    };

    const loadConfigForStand = async (standId) => {
        if (!standId || standId === 0) return;
        const res = await GetConfig(standId);
        if (res.success && res.data) {
            setStandConfig({
                ...res.data,
                winProbability: Math.round(res.data.winProbability * 100),
                scale: res.data.scale || 100,
                themeId: res.data.themeId || ''
            });
        }
    };

    const resetAdminSpinState = (screenId) => {
        setAdminSpinStates(prev => ({
            ...prev,
            [screenId]: { gameState: 'idle', stoppingIndex: 0, isLocked: false }
        }));
    };

    const handleStandChange = (e) => {
        const id = Number(e.target.value);
        setSelectedStandId(id);
        resetAdminSpinState(id);
        loadConfigForStand(id);
    };

    // ==========================================
    // AUTO-GUARDADO
    // ==========================================
    const autoSaveConfig = async (newConfig) => {
        showMessage('Guardando cambios...', MESSAGE_TYPE.INFO);
        const payload = {
            ...newConfig,
            winProbability: newConfig.winProbability / 100, // De 30% a 0.3
            themeId: newConfig.themeId ? Number(newConfig.themeId) : null
        };
        const res = await SaveConfig(payload);
        if (res.success) {
            showMessage('✅ Configuración actualizada', MESSAGE_TYPE.SUCCESS);
        } else {
            showMessage('❌ Error al guardar', MESSAGE_TYPE.ERROR);
        }
    };

    const updateMainConfig = (field, value) => {
        const updated = { ...mainConfig, [field]: value };
        setMainConfig(updated);
        autoSaveConfig(updated);
    };

    const updateStandConfig = (field, value) => {
        if (selectedStandId === 0) return;
        const updated = { ...standConfig, [field]: value };
        setStandConfig(updated);
        autoSaveConfig(updated);
    };

    const handleTempInputChange = (key, value) => setTempInputs(prev => ({ ...prev, [key]: value }));
    
    const applyCustomValue = (isMain, field, key) => {
        const val = Number(tempInputs[key]);
        if (!isNaN(val)) {
            if (isMain) updateMainConfig(field, val);
            else updateStandConfig(field, val);
        }
    };

    const handleKeyDown = (e, isMain, field, key) => { 
        if (e.key === 'Enter') applyCustomValue(isMain, field, key); 
    };

    // ==========================================
    // MULTIMEDIA UPLOAD CON DRAG & DROP
    // ==========================================
    const handleDragAndDropUpload = async (file, type, targetField) => {
        showMessage('Subiendo y optimizando archivo...', MESSAGE_TYPE.INFO);
        const res = await UploadMedia(file, type);
        if (res.success) {
            showMessage('✅ Archivo cargado', MESSAGE_TYPE.SUCCESS);
            if (targetField === 'rollerImages') {
                setThemeForm(prev => ({
                    ...prev,
                    rollerImages: [...prev.rollerImages, res.url]
                }));
            } else {
                setThemeForm(prev => ({
                    ...prev,
                    [targetField]: res.url
                }));
            }
        } else {
            showMessage(`❌ Error: ${res.message}`, MESSAGE_TYPE.ERROR);
        }
    };

    const handleSaveTheme = async () => {
        if (!themeForm.name.trim()) {
            showMessage('El nombre del tema es obligatorio', MESSAGE_TYPE.WARNING);
            return;
        }

        showMessage('Guardando tema...', MESSAGE_TYPE.INFO);
        const res = await SaveTheme(themeForm);
        if (res.success) {
            showMessage('✅ Tema guardado exitosamente', MESSAGE_TYPE.SUCCESS);
            setShowThemeModal(false);
            loadInitialData();
        } else {
            showMessage('❌ Error al guardar tema', MESSAGE_TYPE.ERROR);
        }
    };

    const handleDeleteTheme = (id) => {
        setConfirmConfig({
            message: '¿Estás seguro de que deseas eliminar este tema permanentemente?',
            textYes: 'Sí, eliminar',
            action: async () => {
                const res = await DeleteTheme(id);
                if (res.success) {
                    showMessage('✅ Tema eliminado', MESSAGE_TYPE.SUCCESS);
                    loadInitialData();
                } else {
                    showMessage('Error al eliminar', MESSAGE_TYPE.ERROR);
                }
                setShowConfirmModal(false);
            }
        });
        setShowConfirmModal(true);
    };

    const openEditTheme = async (themeId) => {
        if (themeId === 0) {
            setThemeForm(defaultThemeState);
        } else {
            const res = await GetTheme(themeId);
            if (res.success) {
                setThemeForm(res.data);
            }
        }
        setShowThemeModal(true);
    };

    const getMediaUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http://') || path.startsWith('https://')) return path;
        const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        return `${apiBase}${path}`;
    };

    // ==========================================
    // STANDS CREATION / DELETION
    // ==========================================
    const handleAddStand = async () => {
        const name = prompt("Escribe el nombre del nuevo Stand (ej. Stand Tecate):");
        if (!name || name.trim() === '') return;

        setIsLoading(true);
        showMessage('Creando pantalla...', MESSAGE_TYPE.INFO);
        const res = await CreateMachineScreen({ eventId: 1, name: name.trim() });
        if (res.success) {
            showMessage('✅ Stand registrado', MESSAGE_TYPE.SUCCESS);
            await loadInitialData();
            setIsStandsExpanded(true);
            setSelectedStandId(res.data.id);
            loadConfigForStand(res.data.id);
        } else {
            showMessage('Error al registrar Stand', MESSAGE_TYPE.ERROR);
        }
        setIsLoading(false);
    };

    const confirmDeleteScreen = () => {
        const stand = screensList.find(s => s.id === selectedStandId);
        if (!stand) return;
        setConfirmConfig({
            message: `¿Estás seguro de que deseas eliminar la pantalla "${stand.name}"? No afectará el historial de jugadas.`,
            textYes: 'Borrar',
            action: async () => {
                const res = await DeleteMachineScreen(selectedStandId);
                if (res.success) {
                    showMessage('✅ Pantalla borrada', MESSAGE_TYPE.SUCCESS);
                    await loadInitialData();
                } else {
                    showMessage('Error al borrar', MESSAGE_TYPE.ERROR);
                }
                setShowConfirmModal(false);
            }
        });
        setShowConfirmModal(true);
    };

    const openQRModal = (screenId, connectionToken, name) => {
        if (!checkThemeSelected(screenId)) return;
        const localIP = window.location.hostname;
        const port = window.location.port ? `:${window.location.port}` : '';
        const fanUrl = `http://${localIP}${port}/rollers?screenId=${screenId}`;
        const tvUrl = `http://${localIP}${port}/screen`;

        setQrTargetUrl(fanUrl);
        setTvTargetUrl(tvUrl);
        setQrTitle(name);
        setShowQRModal(true);
    };

    // ==========================================
    // PROJECTIONS & ACTIONS (CON VALIDACIÓN DE TEMA SELECCIONADO)
    // ==========================================
    const checkThemeSelected = (screenId) => {
        const conf = screenId === 1 ? mainConfig : standConfig;
        if (!conf.themeId) {
            showMessage('⚠️ Por favor selecciona un tema activo en la configuración de la pantalla.', MESSAGE_TYPE.WARNING);
            return false;
        }
        return true;
    };

    const handleShowCoverAction = async (screenId) => {
        if (!checkThemeSelected(screenId)) return;
        resetAdminSpinState(screenId);
        showMessage('Mostrando Portada...', MESSAGE_TYPE.INFO);
        const res = await ShowCover(screenId);
        if (res.success) showMessage('✅ Portada mostrada en pantalla', MESSAGE_TYPE.SUCCESS);
        else showMessage(res.message || 'Error', MESSAGE_TYPE.ERROR);
    };

    const handleShowGameAction = async (screenId) => {
        if (!checkThemeSelected(screenId)) return;
        resetAdminSpinState(screenId);
        showMessage('Preparando rodillos...', MESSAGE_TYPE.INFO);
        const res = await PrepareMachine(screenId);
        if (res.success) showMessage('✅ Rodillos listos en pantalla', MESSAGE_TYPE.SUCCESS);
        else showMessage(res.message || 'Error', MESSAGE_TYPE.ERROR);
    };

    const triggerSpin = async (screenId, totalReels = 3) => {
        if (!checkThemeSelected(screenId)) return;
        const state = adminSpinStates[screenId] || { gameState: 'idle', stoppingIndex: 0, isLocked: false };
        if (state.isLocked) return;

        if (state.gameState === 'idle') {
            setAdminSpinStates(prev => ({ ...prev, [screenId]: { ...state, isLocked: true } }));
            showMessage('Iniciando giro...', MESSAGE_TYPE.INFO);
            const res = await SpinMachine({
                targetScreenId: screenId,
                playerName: "Tiro Admin",
                playerContact: "N/A"
            });
            if (res.success) {
                setAdminSpinStates(prev => ({
                    ...prev,
                    [screenId]: { gameState: 'spinning', stoppingIndex: 0, isLocked: false }
                }));
            } else {
                showMessage(res.message || 'Error al girar', MESSAGE_TYPE.ERROR);
                setAdminSpinStates(prev => ({ ...prev, [screenId]: { ...state, isLocked: false } }));
            }
        } else if (state.gameState === 'spinning') {
            setAdminSpinStates(prev => ({ ...prev, [screenId]: { ...state, isLocked: true } }));
            showMessage(`Deteniendo rodillo ${state.stoppingIndex + 1}...`, MESSAGE_TYPE.INFO);
            const res = await StopReel(screenId, state.stoppingIndex);
            if (res.success) {
                const nextIndex = state.stoppingIndex + 1;
                if (nextIndex >= totalReels) {
                    setAdminSpinStates(prev => ({
                        ...prev,
                        [screenId]: { gameState: 'idle', stoppingIndex: 0, isLocked: false }
                    }));
                } else {
                    setAdminSpinStates(prev => ({
                        ...prev,
                        [screenId]: { gameState: 'spinning', stoppingIndex: nextIndex, isLocked: false }
                    }));
                }
            } else {
                showMessage('Error al detener rodillo', MESSAGE_TYPE.ERROR);
                setAdminSpinStates(prev => ({ ...prev, [screenId]: { ...state, isLocked: false } }));
            }
        }
    };

    const confirmApplyToAll = (field, value, fieldName) => {
        setConfirmConfig({
            message: `¿Deseas aplicar el valor de ${fieldName} a TODOS los Stands?`,
            action: async () => {
                showMessage('Aplicando a todos...', MESSAGE_TYPE.INFO);
                const stands = screensList.filter(s => s.id !== 1);
                for (let stand of stands) {
                    await SaveConfig({
                        ...standConfig,
                        targetScreenId: stand.id,
                        [field]: value
                    });
                }
                showMessage('✅ Aplicado a todas las pantallas', MESSAGE_TYPE.SUCCESS);
                return { success: true };
            },
            textYes: 'Sí, aplicar',
            textNo: 'Cancelar'
        });
        setShowConfirmModal(true);
    };

    const renderRulesEditor = (isMain) => {
        const conf = isMain ? mainConfig : standConfig;
        const updateFn = isMain ? updateMainConfig : updateStandConfig;
        const showProb = isMain ? showCustomMainProb : showCustomStandProb;
        const setShowProb = isMain ? setShowCustomMainProb : setShowCustomStandProb;
        const showTimes = isMain ? showCustomMainTimes : showCustomStandTimes;
        const setShowTimes = isMain ? setShowCustomMainTimes : setShowCustomStandTimes;
        const prefix = isMain ? 'main' : 'stand';

        return (
            <div className="bg-white p-3 rounded-4 border shadow-sm text-dark text-start">
                {/* TEMA SELECT */}
                <div className="mb-4">
                    <label className="fw-bold m-0 text-dark d-block mb-2">Tema de Marca Patrocinadora</label>
                    <select 
                        className="form-select fw-bold border-1 text-primary shadow-sm" 
                        value={conf.themeId || ''} 
                        onChange={(e) => updateFn('themeId', e.target.value ? Number(e.target.value) : null)}
                    >
                        <option value="">-- Elige una opción --</option>
                        {themesList.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                </div>

                {/* NÚMERO DE RODILLOS */}
                <div className="mb-4">
                    <label className="fw-bold m-0 text-dark d-block mb-2">Número de Rodillos</label>
                    <div className="d-flex gap-2">
                        <button className={`btn btn-sm ${conf.rollersCount === 3 ? 'btn-primary fw-bold' : 'btn-outline-primary'}`} onClick={() => updateFn('rollersCount', 3)}>3 Rodillos</button>
                        {/* <button className={`btn btn-sm ${conf.rollersCount === 5 ? 'btn-primary fw-bold' : 'btn-outline-primary'}`} onClick={() => updateFn('rollersCount', 5)}>5 Rodillos</button> */}
                    </div>
                </div>

                {/* PROBABILIDAD */}
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="fw-bold m-0 text-dark">Probabilidad de que ganen</label>
                        <button className="btn btn-link text-secondary btn-sm p-0 text-decoration-none" onClick={() => setShowProb(!showProb)}>
                            <i className={`bi ${showProb ? 'bi-eye-slash' : 'bi-sliders'}`}></i> {showProb ? 'Ocultar' : 'Personalizar'}
                        </button>
                    </div>
                    
                    <div className="d-flex gap-2 mb-2 flex-wrap">
                        <button className={`btn btn-sm ${conf.winProbability === 0 ? 'btn-danger fw-bold' : 'btn-outline-danger'}`} onClick={() => updateFn('winProbability', 0)}>Nunca (0%)</button>
                        <button className={`btn btn-sm ${conf.winProbability === 30 ? 'btn-warning fw-bold text-dark' : 'btn-outline-warning text-dark'}`} onClick={() => updateFn('winProbability', 30)}>Difícil (30%)</button>
                        <button className={`btn btn-sm ${conf.winProbability === 50 ? 'btn-primary fw-bold' : 'btn-outline-primary'}`} onClick={() => updateFn('winProbability', 50)}>50/50 (50%)</button>
                        <button className={`btn btn-sm ${conf.winProbability === 100 ? 'btn-success fw-bold' : 'btn-outline-success'}`} onClick={() => updateFn('winProbability', 100)}>Siempre (100%)</button>
                        
                        {!isMain && (
                            <button className="btn btn-sm btn-outline-secondary ms-auto mt-2" onClick={() => confirmApplyToAll('winProbability', conf.winProbability, 'Probabilidad')}>
                                <i className="bi bi-copy"></i> Aplicar a todos
                            </button>
                        )}
                    </div>

                    {showProb && (
                        <div className="input-group input-group-sm w-50 mt-2">
                            <input type="number" className="form-control fw-bold" placeholder="Ej: 75" value={tempInputs[`${prefix}_prob`] || ''} onChange={(e) => handleTempInputChange(`${prefix}_prob`, e.target.value)} onKeyDown={(e) => handleKeyDown(e, isMain, 'winProbability', `${prefix}_prob`)} />
                            <span className="input-group-text bg-light">%</span>
                            <button className="btn btn-primary" onClick={() => applyCustomValue(isMain, 'winProbability', `${prefix}_prob`)}>Aplicar</button>
                        </div>
                    )}
                </div>

                <hr className="text-muted opacity-25" />

                {/* TIEMPOS */}
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="fw-bold m-0 text-dark">Velocidades de la máquina</label>
                        <button className="btn btn-link text-secondary btn-sm p-0 text-decoration-none" onClick={() => setShowTimes(!showTimes)}>
                            <i className={`bi ${showTimes ? 'bi-eye-slash' : 'bi-sliders'}`}></i> {showTimes ? 'Ocultar' : 'Personalizar'}
                        </button>
                    </div>

                    <div className="row g-3 text-start">
                        <div className="col-md-12">
                            <span className="small text-muted mb-1 d-block">Velocidad del giro:</span>
                            <div className="d-flex gap-1 mb-2">
                                <button className={`btn btn-sm ${conf.baseTimeMs === 3500 ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => updateFn('baseTimeMs', 3500)}>Lento</button>
                                <button className={`btn btn-sm ${conf.baseTimeMs === 2200 ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => updateFn('baseTimeMs', 2200)}>Normal</button>
                                <button className={`btn btn-sm ${conf.baseTimeMs === 1500 ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => updateFn('baseTimeMs', 1500)}>Rápido</button>
                            </div>
                            {showTimes && (
                                <div className="input-group input-group-sm mt-1">
                                    <input type="number" className="form-control" placeholder="ms" value={tempInputs[`${prefix}_base`] || ''} onChange={(e) => handleTempInputChange(`${prefix}_base`, e.target.value)} onKeyDown={(e) => handleKeyDown(e, isMain, 'baseTimeMs', `${prefix}_base`)}/>
                                    <button className="btn btn-primary" onClick={() => applyCustomValue(isMain, 'baseTimeMs', `${prefix}_base`)}>Aplicar</button>
                                </div>
                            )}
                            <p className='small text-muted mb-0'>Recomendado: Lento</p>
                        </div>
                        <div className="col-md-12">
                            <span className="small text-muted mb-1 d-block">Tiempo de celebración (si gana):</span>
                            <div className="d-flex gap-1 mb-2">
                                <button className={`btn btn-sm ${conf.celebrationTimeMs === 3000 ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => updateFn('celebrationTimeMs', 3000)}>Corto</button>
                                <button className={`btn btn-sm ${conf.celebrationTimeMs === 6000 ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => updateFn('celebrationTimeMs', 6000)}>Normal</button>
                                <button className={`btn btn-sm ${conf.celebrationTimeMs === 10000 ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => updateFn('celebrationTimeMs', 10000)}>Prolongado</button>
                            </div>
                            {showTimes && (
                                <div className="input-group input-group-sm mt-1">
                                    <input type="number" className="form-control" placeholder="ms" value={tempInputs[`${prefix}_celeb`] || ''} onChange={(e) => handleTempInputChange(`${prefix}_celeb`, e.target.value)} onKeyDown={(e) => handleKeyDown(e, isMain, 'celebrationTimeMs', `${prefix}_celeb`)}/>
                                    <button className="btn btn-primary" onClick={() => applyCustomValue(isMain, 'celebrationTimeMs', `${prefix}_celeb`)}>Aplicar</button>
                                </div>
                            )}
                            <p className='small text-muted mb-0'>Recomendado: Corto</p>
                        </div>
                    </div>
                </div>

                <hr className="text-muted opacity-25" />

                {/* ESCALA / ZOOM */}
                <div>
                    <label className="fw-bold m-0 text-dark d-block mb-2">Tamaño de la Máquina (Zoom)</label>
                    <div className="d-flex align-items-center gap-2">
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => updateFn('scale', Math.max(50, conf.scale - 10))} title="Reducir tamaño"><i className="bi bi-dash-lg"></i></button>
                        <input type="range" className="form-range flex-grow-1" min="50" max="150" step="10" value={conf.scale} onChange={(e) => updateFn('scale', Number(e.target.value))} />
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => updateFn('scale', Math.min(150, conf.scale + 10))} title="Aumentar tamaño"><i className="bi bi-plus-lg"></i></button>
                        <span className="badge bg-light text-dark border ms-2 px-3 py-2 fs-6">{conf.scale}%</span>
                    </div>
                    <button className="btn btn-outline-primary btn-sm mt-3 fw-bold" onClick={() => handleShowGameAction(isMain ? 1 : selectedStandId)}>
                        <i className="bi bi-arrows-fullscreen"></i> Aplicar nuevo tamaño en la pantalla
                    </button>
                </div>
            </div>
        );
    };

    const mainScreenObj = screensList.find(s => s.id === 1);
    const isMainOnline = mainScreenObj?.isOnline === true;

    return (
        <SecondaryBase actionToExecute={getOutOfTheDynamic}>
            
            {showConfirmModal && (
                <Confirm message={confirmConfig.message} actionToYesButton={confirmConfig.action} textYesButton={confirmConfig.textYes} textNoButton={confirmConfig.textNo} setShowModal={setShowConfirmModal} />
            )}

            {showQRModal && (
                <>
                    <div className="cModal__Background" onClick={() => setShowQRModal(false)}></div>
                    <div className="cModal__Container" style={{ zIndex: 99999 }}>
                        <div className="cModal__Container__Content position-relative p-0" style={{ background: 'transparent', boxShadow: 'none' }}>
                            <button type="button" className="btn-close position-absolute top-0 end-0 m-3 bg-dark p-2 rounded-circle animate-pulse" onClick={() => setShowQRModal(false)} style={{ zIndex: 20 }}></button>
                            <div className="bg-dark text-white p-3 rounded-top-4 text-center border-bottom border-secondary">
                                <span className="d-block small text-warning mb-1"><i className="bi bi-tv"></i> Abre este link en tu TV o dispositivo de proyección:</span>
                                <span className="fw-bold user-select-all text-break">{tvTargetUrl}</span>
                            </div>
                            <PremiumQR url={qrTargetUrl} title={qrTitle} />
                        </div>
                    </div>
                </>
            )}

            <div className="mb-4 text-center text-dark">
                <p className="fs-2 text-uppercase fw-bold m-0" style={{ letterSpacing: '2px' }}><i className="bi bi-images text-warning"></i> Arma la Imagen</p>
                <div className="d-inline-flex align-items-center gap-2 bg-white px-3 py-1 rounded-pill border shadow-sm mt-2">
                    <span className="small text-muted">Estado de pantallas actualizado a las: <strong className="text-dark">{lastRefresh}</strong></span>
                    <button className="btn btn-sm btn-light border rounded-circle p-1 d-flex align-items-center justify-content-center" onClick={loadInitialData} title="Refrescar conexiones" style={{ width: '28px', height: '28px' }}>
                        <i className="bi bi-arrow-repeat text-primary"></i>
                    </button>
                </div>
            </div>

            {/* ACORDEÓN 1: ESTADIO */}
            <div className="card shadow-sm border-0 mb-4 rounded-4 overflow-hidden">
                <div 
                    className={`card-header p-3 cursor-pointer d-flex justify-content-between align-items-center ${isMainExpanded ? 'bg-secondary text-white' : 'bg-light text-dark'}`}
                    onClick={() => setIsMainExpanded(!isMainExpanded)}
                >
                    <h5 className="m-0 fw-bold">
                        <i className="bi bi-projector-fill me-2"></i> 🏟️ Pantalla del estadio {isMainOnline ? '🟢' : '🔴'}
                    </h5>
                    <div className="d-flex align-items-center gap-3">
                        <button className="btn btn-success fw-bold border border-secondary" onClick={(e) => { e.stopPropagation(); handleShowGameAction(1); }}>
                            Proyectar juego en pantalla
                        </button>
                        <i className={`bi fs-5 ${isMainExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                    </div>
                </div>

                {!isMainExpanded && (
                    <div className="card-body bg-white d-flex justify-content-between align-items-center border-top text-dark">
                        <span className="text-muted fw-bold">Resumen: <span className="text-primary">{mainConfig.winProbability}% Probabilidad</span> | Rodillos: {mainConfig.rollersCount} | Escala {mainConfig.scale}%</span>
                        <button className="btn btn-outline-primary btn-sm fw-bold" onClick={() => { setIsMainExpanded(true); openQRModal(1, 'estadio', 'Pantalla Principal Estadio'); }}>
                            <i className="bi bi-qr-code"></i> Ver instrucciones de conexión
                        </button>
                    </div>
                )}

                {isMainExpanded && (
                    <div className="card-body bg-light">
                        <div className="row">
                            <div className="col-md-7">{renderRulesEditor(true)}</div>
                            <div className="col-md-5">
                                <div className="bg-white p-3 rounded-4 border shadow-sm h-100 d-flex flex-column justify-content-center">
                                    <h6 className="fw-bold text-muted text-center mb-3">Preparación</h6>
                                    <button className="btn btn-outline-primary fw-bold py-2 border-dashed w-100" onClick={() => openQRModal(1, 'estadio', 'Pantalla Principal Estadio')}><i className="bi bi-qr-code"></i> Ver instrucciones de conexión</button>

                                    <h6 className="fw-bold text-muted text-center mb-3 mt-5">Controles de emergencia</h6>
                                    <div className="d-grid gap-2">
                                        <button 
                                            className="btn btn-success fw-bold py-2 text-white" 
                                            onClick={() => triggerSpin(1, mainConfig.rollersCount)}
                                            disabled={adminSpinStates[1]?.isLocked}
                                        >
                                            <i className="bi bi-play-fill"></i> {adminSpinStates[1]?.gameState === 'spinning' ? `Detener Rodillo ${adminSpinStates[1].stoppingIndex + 1}` : 'Accionar palanca / Girar'}
                                        </button>
                                        <button className="btn btn-outline-warning text-dark fw-bold py-2" onClick={() => handleShowGameAction(1)}><i className="bi bi-arrow-clockwise"></i> Reiniciar rodillos</button>
                                        <button className="btn btn-outline-info text-dark fw-bold py-2" onClick={() => handleShowCoverAction(1)}><i className="bi bi-projector-fill"></i> Mostrar portada</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ACORDEÓN 2: STANDS EXTRA */}
            <div className="card shadow-sm border-0 mb-4 rounded-4 overflow-hidden">
                <div 
                    className={`card-header p-3 cursor-pointer d-flex justify-content-between align-items-center ${isStandsExpanded ? 'bg-secondary text-white' : 'bg-light text-dark'}`}
                    onClick={() => setIsStandsExpanded(!isStandsExpanded)}
                >
                    <h5 className="m-0 fw-bold"><i className="bi bi-tv-fill me-2"></i> 📺 ¿Llevarás el juego a más pantallas? (Stands)</h5>
                    <i className={`bi fs-5 ${isStandsExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                </div>

                {isStandsExpanded && (
                    <div className="card-body bg-light text-dark">
                        <div className="d-flex align-items-center mb-4 bg-white p-3 rounded border shadow-sm flex-wrap gap-2">
                            <label className="fw-bold text-dark mb-0">Selecciona el stand:</label>
                            <select className="form-select fw-bold border-1 text-primary shadow-sm w-auto" value={selectedStandId} onChange={handleStandChange}>
                                {screensList.filter(s => s.id !== 1).length === 0 && <option value="0">-- No hay stands --</option>}
                                {screensList.filter(s => s.id !== 1).map(s => (
                                    <option key={s.id} value={s.id}>
                                        {s.isOnline === true ? '🟢' : '🔴'} {s.name}
                                    </option>
                                ))}
                            </select>
                            
                            <button className="btn btn-dark btn-sm fw-bold" onClick={handleAddStand}>
                                <i className="bi bi-plus-lg"></i> Crear nuevo stand
                            </button>

                            {selectedStandId !== 0 && (
                                <button className="btn btn-outline-danger btn-sm ms-auto fw-bold" onClick={confirmDeleteScreen} title="Eliminar este stand">
                                    <i className="bi bi-trash-fill"></i> Eliminar stand
                                </button>
                            )}
                        </div>

                        {selectedStandId !== 0 ? (
                            <div className="row">
                                <div className="col-md-7">{renderRulesEditor(false)}</div>
                                <div className="col-md-5">
                                    <div className="bg-white p-3 rounded-4 border shadow-sm h-100 d-flex flex-column justify-content-center">
                                        <h6 className="fw-bold text-muted text-center">Preparación</h6>
                                        <button className="btn btn-outline-primary fw-bold py-2 w-100" onClick={() => { const stand = screensList.find(s => s.id === selectedStandId); openQRModal(stand.id, stand.connectionToken, stand.name); }}>
                                            <i className="bi bi-qr-code"></i> Ver instrucciones de conexión
                                        </button>

                                        <h6 className="fw-bold text-muted text-center mb-3 mt-5">Controles de emergencia del stand</h6>
                                        <div className="d-grid gap-2">
                                            <button 
                                                className="btn btn-success fw-bold py-2 text-white" 
                                                onClick={() => triggerSpin(selectedStandId, standConfig.rollersCount)}
                                                disabled={adminSpinStates[selectedStandId]?.isLocked}
                                            >
                                                <i className="bi bi-play-fill"></i> {adminSpinStates[selectedStandId]?.gameState === 'spinning' ? `Detener Rodillo ${adminSpinStates[selectedStandId].stoppingIndex + 1}` : 'Accionar palanca / Girar'}
                                            </button>    
                                            <button className="btn btn-outline-warning text-dark fw-bold py-2" onClick={() => handleShowGameAction(selectedStandId)}><i className="bi bi-arrow-clockwise"></i> Reiniciar rodillos</button>
                                            <button className="btn btn-outline-info text-dark fw-bold py-2" onClick={() => handleShowCoverAction(selectedStandId)}><i className="bi bi-projector-fill"></i> Mostrar portada</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center p-4 text-muted">Crea un nuevo stand para configurar sus reglas.</div>
                        )}
                    </div>
                )}
            </div>

            {/* SECCIÓN DE TEMAS DE MARCA */}
            <div className="card shadow-sm border-0 mb-4 rounded-4 overflow-hidden text-dark text-start">
                <div className="card-header p-3 bg-secondary text-white fw-bold d-flex justify-content-between align-items-center">
                    <h5 className="m-0 fw-bold"><i className="bi bi-palette-fill me-2"></i> Gestión de Temas de Marca</h5>
                    <button className="btn btn-success btn-sm fw-bold border border-secondary" onClick={() => openEditTheme(0)}>
                        <i className="bi bi-plus-lg"></i> Crear nuevo tema
                    </button>
                </div>
                <div className="card-body bg-light">
                    <div className="row">
                        {themesList.length === 0 ? (
                            <div className="col-12 text-center text-muted py-4">
                                No hay temas de marca creados aún. Presiona "Crear nuevo tema".
                            </div>
                        ) : (
                            themesList.map(t => (
                                <div className="col-xl-3 col-lg-4 col-md-6 mb-3" key={t.id}>
                                    <div className="border border-secondary p-3 rounded bg-white shadow-sm h-100 d-flex flex-column justify-content-between">
                                        <div>
                                            <h5 className="fw-bold text-dark mb-1">{t.name}</h5>
                                            <p className="small text-muted mb-3">Imágenes Rodillos: {t.rollerImages?.length || 0}</p>
                                            {t.brandLogoUrl && (
                                                <div className="text-center bg-light p-2 rounded mb-2">
                                                    <img src={getMediaUrl(t.brandLogoUrl)} alt="Brand Logo" className="img-fluid" style={{ maxHeight: '40px', objectFit: 'contain' }} />
                                                </div>
                                            )}
                                        </div>
                                        <div className="d-flex gap-2 w-100 mt-2">
                                            <button className="btn btn-sm btn-outline-warning flex-fill fw-bold text-dark" onClick={() => openEditTheme(t.id)}>
                                                <i className="bi bi-pencil-fill"></i> Editar
                                            </button>
                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteTheme(t.id)}>
                                                <i className="bi bi-trash3-fill"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* BOTÓN GLOBAL DE IDENTIFICACIÓN */}
            <div className="text-center mt-4">
                <button className="btn btn-outline-dark fw-bold shadow-sm" onClick={() => { IdentifyAllScreens(); showMessage('Enviando señal a las pantallas...', MESSAGE_TYPE.INFO); }}>
                    <i className="bi bi-search"></i> Identificar todas las pantallas físicas
                </button>
                <p className="small text-muted mt-2">Útil si tienes varias pantallas conectadas y no sabes cuál es cuál.</p>
            </div>

            {/* MODAL CREAR/EDITAR TEMA */}
            {showThemeModal && (
                <div className="modal d-block text-dark" style={{ backgroundColor: 'rgba(0,0,0,0.85)', overflowY: 'auto', zIndex: 9999 }}>
                    <div className="modal-dialog modal-lg text-start">
                        <div className="modal-content bg-white">
                            <div className="modal-header bg-light border-bottom">
                                <h5 className="modal-title fw-bold text-uppercase text-primary">
                                    {themeForm.id === 0 ? 'Crear Nuevo Tema de Marca' : `Editar Tema: ${themeForm.name}`}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowThemeModal(false)}></button>
                            </div>
                            <div className="modal-body text-dark">
                                
                                {/* NOMBRE DEL TEMA */}
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Nombre del Tema:</label>
                                    <input 
                                        type="text" 
                                        className="form-control border-secondary"
                                        placeholder="Ej: Corona Verano 2026, ADSL Navidad"
                                        value={themeForm.name}
                                        onChange={(e) => setThemeForm({ ...themeForm, name: e.target.value })}
                                    />
                                </div>

                                <div className="row">
                                    {/* IMAGEN DE LA MARCA (CORONA) */}
                                    <div className="col-md-6 mb-3">
                                        <DragAndDropField 
                                            label="Logotipo Superior de Marca (e.g. Corona)" 
                                            targetField="brandLogoUrl" 
                                            type="logo" 
                                            value={themeForm.brandLogoUrl} 
                                            onUpload={handleDragAndDropUpload} 
                                            getMediaUrl={getMediaUrl} 
                                        />
                                    </div>

                                    {/* LOGO PATROCINADOR PORTADA */}
                                    <div className="col-md-6 mb-3">
                                        <DragAndDropField 
                                            label="Logotipo Patrocinador (Portada)" 
                                            targetField="sponsorLogoUrl" 
                                            type="logo" 
                                            value={themeForm.sponsorLogoUrl} 
                                            onUpload={handleDragAndDropUpload} 
                                            getMediaUrl={getMediaUrl} 
                                        />
                                    </div>
                                </div>

                                {/* 1. PORTADA BACKGROUND */}
                                <div className="border p-3 rounded mb-3 bg-light">
                                    <div className="row align-items-end">
                                        <div className="col-md-4 mb-2">
                                            <label className="fw-bold mb-1">Tipo de Fondo de Portada:</label>
                                            <select className="form-select border-secondary" value={themeForm.coverBackgroundType} onChange={(e) => setThemeForm({ ...themeForm, coverBackgroundType: e.target.value })}>
                                                <option value="color">Solo Color</option>
                                                <option value="image">Imagen de Fondo</option>
                                                <option value="video">Video de Fondo (MP4)</option>
                                            </select>
                                        </div>
                                        <div className="col-md-8 mb-2">
                                            {themeForm.coverBackgroundType === 'color' ? (
                                                <>
                                                    <label className="fw-bold mb-1">Color de Fondo:</label>
                                                    <input type="color" className="form-control w-100" style={{ height: '38px' }} value={themeForm.coverBackgroundColor} onChange={(e) => setThemeForm({ ...themeForm, coverBackgroundColor: e.target.value })} />
                                                </>
                                            ) : (
                                                <DragAndDropField 
                                                    label="Archivo Multimedia de Fondo" 
                                                    targetField="coverBackgroundUrl" 
                                                    type="background" 
                                                    value={themeForm.coverBackgroundUrl} 
                                                    onUpload={handleDragAndDropUpload} 
                                                    getMediaUrl={getMediaUrl} 
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* 2. FONDO DE JUEGO */}
                                <div className="border p-3 rounded mb-3 bg-light">
                                    <div className="row align-items-end">
                                        <div className="col-md-4 mb-2">
                                            <label className="fw-bold mb-1">Tipo de Fondo de Juego:</label>
                                            <select className="form-select border-secondary" value={themeForm.gameBackgroundType} onChange={(e) => setThemeForm({ ...themeForm, gameBackgroundType: e.target.value })}>
                                                <option value="color">Solo Color</option>
                                                <option value="image">Imagen de Fondo</option>
                                                <option value="video">Video de Fondo (MP4)</option>
                                            </select>
                                        </div>
                                        <div className="col-md-8 mb-2">
                                            {themeForm.gameBackgroundType === 'color' ? (
                                                <>
                                                    <label className="fw-bold mb-1">Color de Fondo:</label>
                                                    <input type="color" className="form-control w-100" style={{ height: '38px' }} value={themeForm.gameBackgroundColor} onChange={(e) => setThemeForm({ ...themeForm, gameBackgroundColor: e.target.value })} />
                                                </>
                                            ) : (
                                                <DragAndDropField 
                                                    label="Archivo Multimedia de Fondo" 
                                                    targetField="gameBackgroundUrl" 
                                                    type="background" 
                                                    value={themeForm.gameBackgroundUrl} 
                                                    onUpload={handleDragAndDropUpload} 
                                                    getMediaUrl={getMediaUrl} 
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* 3. FONDO DE LA MÁQUINA */}
                                <div className="border p-3 rounded mb-3 bg-light">
                                    <div className="row align-items-end">
                                        <div className="col-md-4 mb-2">
                                            <label className="fw-bold mb-1">Tipo de Fondo de Estructura:</label>
                                            <select className="form-select border-secondary" value={themeForm.machineBackgroundType} onChange={(e) => setThemeForm({ ...themeForm, machineBackgroundType: e.target.value })}>
                                                <option value="color">Solo Color</option>
                                                <option value="image">Imagen de Fondo</option>
                                            </select>
                                        </div>
                                        <div className="col-md-8 mb-2">
                                            {themeForm.machineBackgroundType === 'color' ? (
                                                <>
                                                    <label className="fw-bold mb-1">Color de Fondo:</label>
                                                    <input type="color" className="form-control w-100" style={{ height: '38px' }} value={themeForm.machineBackgroundColor} onChange={(e) => setThemeForm({ ...themeForm, machineBackgroundColor: e.target.value })} />
                                                </>
                                            ) : (
                                                <DragAndDropField 
                                                    label="Archivo de Fondo de Estructura" 
                                                    targetField="machineBackgroundUrl" 
                                                    type="background" 
                                                    value={themeForm.machineBackgroundUrl} 
                                                    onUpload={handleDragAndDropUpload} 
                                                    getMediaUrl={getMediaUrl} 
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* 4. FONDO DEL AFICIONADO */}
                                <div className="border p-3 rounded mb-3 bg-light">
                                    <div className="row align-items-end">
                                        <div className="col-md-4 mb-2">
                                            <label className="fw-bold mb-1">Tipo de Fondo del Aficionado:</label>
                                            <select className="form-select border-secondary" value={themeForm.fanBackgroundType} onChange={(e) => setThemeForm({ ...themeForm, fanBackgroundType: e.target.value })}>
                                                <option value="color">Solo Color</option>
                                                <option value="image">Imagen de Fondo</option>
                                                <option value="video">Video de Fondo (MP4)</option>
                                            </select>
                                        </div>
                                        <div className="col-md-8 mb-2">
                                            {themeForm.fanBackgroundType === 'color' ? (
                                                <>
                                                    <label className="fw-bold mb-1">Color de Fondo:</label>
                                                    <input type="color" className="form-control w-100" style={{ height: '38px' }} value={themeForm.fanBackgroundColor} onChange={(e) => setThemeForm({ ...themeForm, fanBackgroundColor: e.target.value })} />
                                                </>
                                            ) : (
                                                <DragAndDropField 
                                                    label="Archivo Multimedia de Fondo" 
                                                    targetField="fanBackgroundUrl" 
                                                    type="background" 
                                                    value={themeForm.fanBackgroundUrl} 
                                                    onUpload={handleDragAndDropUpload} 
                                                    getMediaUrl={getMediaUrl} 
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <hr className="border-secondary" />

                                {/* IMÁGENES DE LOS RODILLOS */}
                                <div className="mb-3">
                                    <label className="form-label fw-bold text-primary fs-5">Imágenes de los Rodillos (Se recortarán a 600x600px automáticamente):</label>
                                    <DragAndDropField 
                                        label="" 
                                        targetField="rollerImages" 
                                        type="roller" 
                                        value="" 
                                        onUpload={handleDragAndDropUpload} 
                                        getMediaUrl={getMediaUrl} 
                                    />
                                    
                                    <div className="row mt-3">
                                        {themeForm.rollerImages.map((imgUrl, index) => (
                                            <div className="col-3 mb-2 text-center position-relative" key={index}>
                                                <div className="border border-secondary p-1 rounded bg-black">
                                                    <img src={getMediaUrl(imgUrl)} alt="Roller symbol" className="img-fluid" style={{ maxHeight: '100px', objectFit: 'contain' }} />
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-sm btn-danger position-absolute top-0 end-0 rounded-circle"
                                                        onClick={() => setThemeForm({
                                                            ...themeForm,
                                                            rollerImages: themeForm.rollerImages.filter((_, i) => i !== index)
                                                        })}
                                                    >
                                                        &times;
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                            </div>
                            <div className="modal-footer border-top bg-light">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowThemeModal(false)}>Cancelar</button>
                                <button type="button" className="btn btn-primary fw-bold px-4" onClick={handleSaveTheme}>
                                    <i className="bi bi-save-fill"></i> Guardar Tema
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </SecondaryBase>
    );
};
